import { guards } from '@shared/helpers/typeguards.helpers';
import type {
  MathJSBaseTypeToUnitCategoryData,
  MathJSUnitInstance,
} from '../mathjs.model';
import { UnitCategory } from '@shared/models/units/unit-base.model';
import { unitToMathJsBaseTypeTranslationMap } from '../mathjs.model';
import { getCanonicalCurrencyUnit } from '@shared/models/units/currency-units.model';

const getCategoryKey = ({ type, power }: { type: string; power: number }) =>
  `${type}^${power}`;

const mathJSBaseTypeToTranslationObjectMap = Object.entries(
  unitToMathJsBaseTypeTranslationMap,
).reduce(
  (acc, [key, value]) => {
    const { mathJsBaseType, power } = value;
    acc[getCategoryKey({ type: mathJsBaseType, power })] = value;
    acc[getCategoryKey({ type: key, power })] = value;
    return acc;
  },
  {} as Record<string, MathJSBaseTypeToUnitCategoryData>,
);

/**
 * Get the base type of a mathJS unit.
 * Only works for units with one unit. (no ratios or compound units)
 * @param mathJSUnit - The mathJS unit to get the base type of.
 * @returns The base type of the mathJS unit or undefined if the unit is a ratio or compound unit.
 */
export const getMathJSUnitCategory = (
  mathJSUnit: MathJSUnitInstance,
): UnitCategory | undefined => {
  const units = mathJSUnit.units;
  if (!guards.is.nonEmptyArray(units)) return undefined;
  if (units.length > 1) return undefined; // compound unit or ratio
  const [unit] = units;
  const nameKey = getCategoryKey({
    type: getCanonicalCurrencyUnit(unit.unit.name), // if unit is not a currency, this will return the original unit
    power: unit.power,
  });
  const baseKey = getCategoryKey({
    type: unit.unit.base.key,
    power: unit.power,
  });
  const translationObject =
    mathJSBaseTypeToTranslationObjectMap[nameKey] ??
    mathJSBaseTypeToTranslationObjectMap[baseKey];

  if (!translationObject) return undefined;

  return translationObject.nodonCategory;
};
